import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Progress, Row, Tooltip, message } from 'antd';
import { MentionsInput, Mention } from 'react-mentions';
import {
	AudioOutlined,
	CameraOutlined,
	DeleteOutlined,
	SendOutlined,
	VideoCameraAddOutlined,
} from '@ant-design/icons';
import { compareStrings, nullSafeGet, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { parseMessageToElements } from '../../utils/ChatUtils';
import { imageMimeTypes, imageUploadValidation } from '../../utils/ImageUtils';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { ImageThumbnails } from '../image_thumbnails/ImageThumbnails';
import { UserName } from '../name_component/user/UserNameComponent';
import ChatUploader from '../ChatInput/ChatUploader';
import AudioRecorder from '../audio_recorder/AudioRecorder';
import moment from 'moment';
import './RowChatInput.less';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getNewNoteType } from '../../utils/WebSocketUtils';

const extractUploadedUrl = (data: any): string => {
	if (typeof data === 'string') return data;
	return nullSafeGetOrElse('cloudFrontUrl', data, '') || nullSafeGetOrElse('fileUrl', data, '');
};

const videoMimeTypes = [
	'video/3gpp',
	'video/mp4',
	'video/mpeg',
	'video/ogg',
	'video/quicktime',
	'video/webm',
	'video/x-m4v',
	'video/ms-asf',
	'video/x-ms-wmv',
	'video/x-msvideo',
];

interface RowChatInputProps {
	sendNote: (entity: any) => Promise<any>;
	onMentionSearch: (search: string, cb: (results: any[]) => void) => void;
	currentUser: any;
	entityId: string | number;
	entityLabel?: string;
	customUploadRequest?: (args: any) => void;
	onWsSendNote?: (text?: string, noteType?: string) => void;
	onWsTyping?: () => void;
	onNoteSent?: (data: any) => void;
	onCancel?: () => void;
	autoFocus?: boolean;
}

const RowChatInput: React.FC<RowChatInputProps> = ({
	sendNote,
	onMentionSearch,
	currentUser,
	entityId,
	entityLabel,
	customUploadRequest,
	onWsSendNote,
	onWsTyping,
	onNoteSent,
	onCancel,
	autoFocus = false,
}) => {
	const wrappedSendNote = useCallback(
		(entity: any) => {
			return sendNote(entity)
				.then((data: any) => {
					message.success('Note sent');
					if (onWsSendNote) onWsSendNote(entity.text || undefined, getNewNoteType(entity));
					if (onNoteSent) onNoteSent(data);
					return data;
				})
				.catch((err: any) => {
					message.error('Failed to send note');
					throw err;
				});
		},
		[sendNote, onWsSendNote, onNoteSent]
	);

	// Media upload state
	const [photoFileList, setPhotoFileList] = useState<any[]>([]);
	const [photoLinks, setPhotoLinks] = useState<string[]>([]);
	const [photoPreviewModalVisible, setPhotoPreviewModalVisible] = useState(false);
	const [videoFileList, setVideoFileList] = useState<any[]>([]);
	const [videoLink, setVideoLink] = useState('');
	const [videoPreviewModalVisible, setVideoPreviewModalVisible] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const [uploadingFileCount, setUploadingFileCount] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);

	const handlePhotoUploadChange = useCallback((info: any) => {
		setPhotoFileList(info.fileList);
		if (info.fileList.some((file: any) => compareStrings(file.status, 'uploading'))) {
			const activeFiles = info.fileList.filter(
				(file: any) => file.status === 'uploading' || file.status === 'done'
			);
			const totalFileCount = activeFiles.length;
			const totalProgress = activeFiles.reduce((sum: number, file: any) => {
				const progress = file.status === 'done' ? 100 : nullSafeGetOrElse('percent', file, 0);
				return sum + progress;
			}, 0);
			const averageProgress = totalFileCount > 0 ? Math.round(totalProgress / totalFileCount) : 0;
			setFileUploading(true);
			setUploadingFileCount(totalFileCount);
			setUploadProgress(averageProgress);
			return;
		}
		if (info.fileList.every((file: any) => compareStrings(file.status, 'done'))) {
			const links = info.fileList.map((file: any) =>
				extractUploadedUrl(nullSafeGet('response.data', file))
			);
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setPhotoLinks(links || []);
			setPhotoPreviewModalVisible(true);
			return;
		}
	}, []);

	const onPhotoSubmit = useCallback(() => {
		const entities = photoLinks.map((photoLink) => ({
			text: '',
			photo: photoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		}));
		entities.forEach((entity) => wrappedSendNote(entity));
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, [photoLinks, currentUser, wrappedSendNote]);

	const onPhotoCancel = useCallback(() => {
		setPhotoLinks([]);
		setPhotoPreviewModalVisible(false);
		setPhotoFileList([]);
	}, []);

	const handleVideoUploadChange = useCallback((info: any) => {
		setVideoFileList(info.fileList);
		if (info.file.status === 'uploading') {
			const progress = nullSafeGetOrElse('file.percent', info, 0);
			setFileUploading(true);
			setUploadingFileCount(1);
			setUploadProgress(Math.round(progress));
			return;
		}
		if (info.file.status === 'done') {
			const link = extractUploadedUrl(nullSafeGet('file.response.data', info));
			setFileUploading(false);
			setUploadingFileCount(0);
			setUploadProgress(0);
			setVideoLink(link);
			setVideoPreviewModalVisible(true);
		}
	}, []);

	const onVideoSubmit = useCallback(() => {
		const entity = {
			text: '',
			video: videoLink,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
		};
		wrappedSendNote(entity);
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, [videoLink, currentUser, wrappedSendNote]);

	const onVideoCancel = useCallback(() => {
		setVideoLink('');
		setVideoPreviewModalVisible(false);
		setVideoFileList([]);
	}, []);

	const handleSendAudio = useCallback(
		(blob: Blob, file: File) => {
			if (customUploadRequest) {
				setFileUploading(true);
				setUploadingFileCount(1);
				setUploadProgress(0);
				customUploadRequest({
					userType: currentUser.userType,
					file,
					onProgress: ({ percent }: any) => {
						setUploadProgress(percent);
					},
					onSuccess: ({ data: audioLink }: any) => {
						setFileUploading(false);
						setUploadingFileCount(0);
						setUploadProgress(0);
						const entity = {
							text: '',
							audio: extractUploadedUrl(audioLink),
							noteAddedBy: currentUser.email,
							noteAddedAt: moment.utc(),
							noteAddedByContact: currentUser,
						};
						wrappedSendNote(entity);
					},
					onError: () => {
						setFileUploading(false);
						setUploadingFileCount(0);
						setUploadProgress(0);
					},
					source: 'RowChatInput',
				});
			} else {
				// Fallback: import dynamically for backward compat (should not happen in practice)
				const { workOrderChatUploadCustomRequest } = require('../../thunks/file_thunks');
				setFileUploading(true);
				setUploadingFileCount(1);
				setUploadProgress(0);
				workOrderChatUploadCustomRequest({
					userType: currentUser.userType,
					workOrderId: entityId,
					file,
					onProgress: ({ percent }: any) => {
						setUploadProgress(percent);
					},
					onSuccess: ({ data: audioLink }: any) => {
						setFileUploading(false);
						setUploadingFileCount(0);
						setUploadProgress(0);
						const entity = {
							text: '',
							audio: audioLink,
							noteAddedBy: currentUser.email,
							noteAddedAt: moment.utc(),
							noteAddedByContact: currentUser,
						};
						wrappedSendNote(entity);
					},
					onError: () => {
						setFileUploading(false);
						setUploadingFileCount(0);
						setUploadProgress(0);
					},
					source: 'RowChatInput',
				});
			}
		},
		[currentUser, entityId, wrappedSendNote, customUploadRequest]
	);

	const [noteText, setNoteText] = useState('');
	const [sending, setSending] = useState(false);
	const containerRef = useRef<HTMLFormElement | null>(null);

	// Auto-focus the text input on mount
	useEffect(() => {
		if (autoFocus && containerRef.current) {
			const textarea = containerRef.current.querySelector('textarea');
			if (textarea) textarea.focus();
		}
	}, [autoFocus]);

	const handleSubmit = (e: any) => {
		if (e) stopDefaultEvents(e);
		if (sending) return;
		const trimmedText = noteText.trim();
		if (!trimmedText) return;

		const { elements, text } = parseMessageToElements(noteText);
		const entity = {
			text,
			noteAddedBy: currentUser.email,
			noteAddedByContact: currentUser,
			noteAddedAt: moment.utc(),
			elements,
		};

		const previousText = noteText;
		setNoteText('');
		setSending(true);
		wrappedSendNote(entity)
			.catch(() => {
				setNoteText(previousText);
			})
			.finally(() => {
				setSending(false);
			});
	};

	const beforeVideoUpload = (file: any) => {
		const isVideo = videoMimeTypes.indexOf(file.type) !== -1;
		if (!isVideo) {
			message.error("This file either isn't a video or is not in a file type we support.");
		}
		return isVideo;
	};

	const hasText = noteText && noteText.trim().length > 0;
	const multiplePhotos = photoLinks.length > 1;
	const label = entityLabel || String(entityId);

	return (
		<div className="w-full">
			{/* Photo preview modal */}
			<Modal
				visible={photoPreviewModalVisible}
				width={600}
				title={multiplePhotos ? `Send these photos to ${label}?` : `Send this photo to ${label}?`}
				okText="Send"
				onCancel={onPhotoCancel}
				onOk={onPhotoSubmit}
				closable={false}
			>
				<Row className="gap-3">
					{photoLinks.map((photoLink, idx) => (
						<ImageThumbnails
							key={idx}
							imageWidth={100}
							imageHeight={100}
							photoStrings={[photoLink]}
						/>
					))}
				</Row>
			</Modal>

			{/* Video preview modal */}
			<Modal
				visible={videoPreviewModalVisible}
				width={600}
				title={`Send this video to ${label}?`}
				okText="Send"
				onCancel={onVideoCancel}
				onOk={onVideoSubmit}
				closable={false}
			>
				<video width="100%" height="auto" controls>
					<source src={videoLink} type={getFileMimeTypeFromString(videoLink)} />
					Your browser does not support the video tag.
				</video>
			</Modal>

			{/* Main input row */}
			<form
				ref={containerRef}
				onSubmit={handleSubmit}
				className="flex items-end gap-2 rounded border border-solid border-grey-5 bg-white px-2 py-1"
			>
				<div className="flex shrink-0 items-center gap-1">
					<Tooltip title="Cancel">
						<Button shape="circle" icon={<DeleteOutlined />} onClick={onCancel}></Button>
					</Tooltip>
					<>
						<ChatUploader
							name="file"
							multiple={true}
							showUploadList={false}
							beforeUpload={imageUploadValidation}
							onChange={handlePhotoUploadChange}
							id={entityId}
							accept={imageMimeTypes}
							fileList={photoFileList}
							customUploadRequest={customUploadRequest}
						>
							<Tooltip title="Upload photo">
								<Button shape="circle" icon={<CameraOutlined />}></Button>
							</Tooltip>
						</ChatUploader>
						<ChatUploader
							name="file"
							showUploadList={false}
							beforeUpload={beforeVideoUpload}
							onChange={handleVideoUploadChange}
							accept={videoMimeTypes.join(',')}
							id={entityId}
							fileList={videoFileList}
							customUploadRequest={customUploadRequest}
						>
							<Tooltip title="Upload video">
								<Button shape="circle" icon={<VideoCameraAddOutlined />}></Button>
							</Tooltip>
						</ChatUploader>
					</>
				</div>

				{/* Text input */}
				<div className="flex-1">
					<MentionsInput
						placeholder="Type a reply..."
						forceSuggestionsAboveCursor
						suggestionsPortalHost={document.body}
						value={noteText}
						onChange={(e, newValue) => {
							setNoteText(newValue);
							if (onWsTyping) onWsTyping();
						}}
						className="row-chat-input"
						allowSpaceInQuery={true}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								stopDefaultEvents(e);
								handleSubmit(e);
							}
							if (e.key === 'Escape' && onCancel) {
								stopDefaultEvents(e);
								onCancel();
							}
						}}
					>
						<Mention
							trigger="@"
							data={onMentionSearch}
							className="bg-[#d2d9de]"
							renderSuggestion={(user, search, highlightedDisplay, index, focused) => {
								const isInactive = compareStrings(user.status, 'inactive');
								return (
									<div
										className={
											'flex flex-row items-center p-2 px-4 ' +
											(isInactive ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
										}
										onClick={(e) => {
											if (isInactive) {
												e.preventDefault();
												e.stopPropagation();
											}
										}}
									>
										<UserName
											contact={user}
											onClick={() => {}}
											status={user.status === 'not_available' ? undefined : user.status}
										/>
									</div>
								);
							}}
						/>
					</MentionsInput>
				</div>

				<div className="flex shrink-0 items-center">
					{fileUploading ? (
						<Tooltip
							title={
								'Uploading ' + uploadingFileCount + (uploadingFileCount === 1 ? ' file' : ' files')
							}
						>
							<Progress type="circle" percent={uploadProgress} width={30} />
						</Tooltip>
					) : hasText || sending ? (
						<Tooltip title={sending ? 'Sending…' : 'Send'}>
							<Button
								shape="circle"
								className="border-none"
								onClick={handleSubmit}
								loading={sending}
								disabled={sending}
								icon={sending ? undefined : <SendOutlined />}
							/>
						</Tooltip>
					) : (
						<AudioRecorder
							popoverPlacement="topRight"
							sendLabel="Send"
							modalTitle="Send Audionote"
							containerType="popover"
							onSend={handleSendAudio}
						>
							<Tooltip title="Record voice note">
								<Button
									shape="circle"
									className="border-none"
									icon={<AudioOutlined className="text-character-secondary" />}
								/>
							</Tooltip>
						</AudioRecorder>
					)}
				</div>
			</form>
		</div>
	);
};

export default RowChatInput;
